import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Upload, 
  Plus, 
  Search, 
  Pencil, 
  Trash2,
  FileSpreadsheet,
  Users
} from "lucide-react";
import * as XLSX from "xlsx";

export default function Clienti() {
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [formData, setFormData] = useState({
    nominativo: "",
    iban: "",
    codice_fiscale_piva: "",
    id_mandato: "",
    data_firma_mandato: "",
    tipo_sequenza: "RCUR",
    codice_causale: "PADD"
  });
  const fileInputRef = useRef(null);

  const fetchClienti = async () => {
    try {
      const res = await axios.get(`${API}/clienti`);
      setClienti(res.data);
    } catch (e) {
      toast.error("Errore nel caricamento clienti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClienti();
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      toast.loading("Importazione in corso...");
      const res = await axios.post(`${API}/clienti/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.dismiss();
      toast.success(`Importati ${res.data.imported} clienti`);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} errori durante l'importazione`);
      }
      fetchClienti();
    } catch (e) {
      toast.dismiss();
      toast.error("Errore durante l'importazione");
    }
    e.target.value = "";
  };

  const handleAdd = async () => {
    try {
      await axios.post(`${API}/clienti`, formData);
      toast.success("Cliente aggiunto");
      setShowAddDialog(false);
      resetForm();
      fetchClienti();
    } catch (e) {
      toast.error("Errore nell'aggiunta del cliente");
    }
  };

  const handleEdit = async () => {
    try {
      await axios.put(`${API}/clienti/${selectedCliente.id}`, formData);
      toast.success("Cliente aggiornato");
      setShowEditDialog(false);
      resetForm();
      fetchClienti();
    } catch (e) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/clienti/${selectedCliente.id}`);
      toast.success("Cliente eliminato");
      setShowDeleteDialog(false);
      setSelectedCliente(null);
      fetchClienti();
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const resetForm = () => {
    setFormData({
      nominativo: "",
      iban: "",
      codice_fiscale_piva: "",
      id_mandato: "",
      data_firma_mandato: "",
      tipo_sequenza: "RCUR",
      codice_causale: "PADD"
    });
    setSelectedCliente(null);
  };

  const openEditDialog = (cliente) => {
    setSelectedCliente(cliente);
    setFormData({
      nominativo: cliente.nominativo,
      iban: cliente.iban,
      codice_fiscale_piva: cliente.codice_fiscale_piva || "",
      id_mandato: cliente.id_mandato,
      data_firma_mandato: cliente.data_firma_mandato,
      tipo_sequenza: cliente.tipo_sequenza,
      codice_causale: cliente.codice_causale
    });
    setShowEditDialog(true);
  };

  const filteredClienti = clienti.filter(c => 
    c.nominativo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.iban.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const downloadTemplate = () => {
    const template = [
      {
        "Nominativo": "Mario Rossi SRL",
        "IBAN": "IT60X0542811101000000123456",
        "Partita IVA/ Cod. fiscale": "12345678901",
        "ID Mandato": "MND-001",
        "Data di firma": "2024-01-15",
        "Tipo di sequenza incasso": "RCUR",
        "Codice causale": "PADD"
      }
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Anagrafiche");
    XLSX.writeFile(wb, "template_anagrafiche.xlsx");
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="clienti-title">
            Anagrafiche Clienti
          </h1>
          <p className="text-slate-500 mt-1">Gestione mandati SEPA B2B</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={downloadTemplate}
            data-testid="download-template-btn"
            className="gap-2"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Template
          </Button>
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            data-testid="import-btn"
            className="gap-2"
          >
            <Upload className="w-4 h-4" />
            Importa Excel
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button 
            onClick={() => setShowAddDialog(true)}
            data-testid="add-cliente-btn"
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            Nuovo Cliente
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Lista Clienti ({filteredClienti.length})
            </CardTitle>
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per nome o IBAN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-clienti"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />
              ))}
            </div>
          ) : filteredClienti.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nessun cliente trovato</p>
              <p className="text-sm text-slate-400 mt-1">
                Importa un file Excel o aggiungi manualmente
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full data-table" data-testid="clienti-table">
                <thead>
                  <tr>
                    <th>Nominativo</th>
                    <th>IBAN</th>
                    <th>P.IVA/CF</th>
                    <th>ID Mandato</th>
                    <th>Data Firma</th>
                    <th>Sequenza</th>
                    <th className="text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClienti.map((cliente) => (
                    <tr key={cliente.id} data-testid={`cliente-row-${cliente.id}`}>
                      <td className="font-medium">{cliente.nominativo}</td>
                      <td className="font-mono text-xs">{cliente.iban}</td>
                      <td className="text-slate-500">{cliente.codice_fiscale_piva || "-"}</td>
                      <td className="font-mono text-xs">{cliente.id_mandato}</td>
                      <td>{cliente.data_firma_mandato}</td>
                      <td>
                        <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                          {cliente.tipo_sequenza}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(cliente)}
                            data-testid={`edit-cliente-${cliente.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedCliente(cliente);
                              setShowDeleteDialog(true);
                            }}
                            data-testid={`delete-cliente-${cliente.id}`}
                            className="text-rose-600 hover:text-rose-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nominativo">Nominativo *</Label>
              <Input
                id="nominativo"
                value={formData.nominativo}
                onChange={(e) => setFormData({ ...formData, nominativo: e.target.value })}
                data-testid="input-nominativo"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="iban">IBAN *</Label>
              <Input
                id="iban"
                value={formData.iban}
                onChange={(e) => setFormData({ ...formData, iban: e.target.value.toUpperCase().replace(/\s/g, '') })}
                className="font-mono"
                data-testid="input-iban"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cf">P.IVA / Codice Fiscale</Label>
              <Input
                id="cf"
                value={formData.codice_fiscale_piva}
                onChange={(e) => setFormData({ ...formData, codice_fiscale_piva: e.target.value })}
                data-testid="input-cf"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mandato">ID Mandato *</Label>
                <Input
                  id="mandato"
                  value={formData.id_mandato}
                  onChange={(e) => setFormData({ ...formData, id_mandato: e.target.value })}
                  data-testid="input-mandato"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data_firma">Data Firma *</Label>
                <Input
                  id="data_firma"
                  type="date"
                  value={formData.data_firma_mandato}
                  onChange={(e) => setFormData({ ...formData, data_firma_mandato: e.target.value })}
                  data-testid="input-data-firma"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleAdd} data-testid="save-cliente-btn">
              Salva Cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifica Cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-nominativo">Nominativo *</Label>
              <Input
                id="edit-nominativo"
                value={formData.nominativo}
                onChange={(e) => setFormData({ ...formData, nominativo: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-iban">IBAN *</Label>
              <Input
                id="edit-iban"
                value={formData.iban}
                onChange={(e) => setFormData({ ...formData, iban: e.target.value.toUpperCase().replace(/\s/g, '') })}
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-cf">P.IVA / Codice Fiscale</Label>
              <Input
                id="edit-cf"
                value={formData.codice_fiscale_piva}
                onChange={(e) => setFormData({ ...formData, codice_fiscale_piva: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-mandato">ID Mandato *</Label>
                <Input
                  id="edit-mandato"
                  value={formData.id_mandato}
                  onChange={(e) => setFormData({ ...formData, id_mandato: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-data-firma">Data Firma *</Label>
                <Input
                  id="edit-data-firma"
                  type="date"
                  value={formData.data_firma_mandato}
                  onChange={(e) => setFormData({ ...formData, data_firma_mandato: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleEdit} data-testid="update-cliente-btn">
              Aggiorna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina Cliente</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare {selectedCliente?.nominativo}? 
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-btn"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
